import React from "react";
import { Radar, Clock, RotateCcw, Receipt, CheckCircle2, XCircle } from "lucide-react";

const BLOCKS = [
  {
    icon: Radar,
    title: "Identify blockers early",
    body: "Detect MDM risk, boot problems, storage failure, missing drivers, BitLocker, Secure Boot issues, and image compatibility problems before processing begins.",
  },
  {
    icon: Clock,
    title: "Protect technician time",
    body: "Give technicians a clear next action instead of leaving them to troubleshoot manually.",
  },
  {
    icon: RotateCcw,
    title: "Avoid unnecessary rework",
    body: "Stop devices from entering the wrong workflow when they should be quarantined, repaired, or reviewed first.",
  },
  {
    icon: Receipt,
    title: "Support fairer billing logic",
    body: "Build workflows around completed processing milestones, not surprise failures.",
  },
];

const Preflight = () => {
  return (
    <section id="preflight" className="relative bg-white font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* left: copy + blocks */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
              Preflight-first
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
              Check First. Process With Confidence.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
              Furloop is designed to cut wasted processing attempts by checking the device <em>before</em>{" "}
              your team starts critical workflows.
            </p>

            <div className="mt-8 space-y-4">
              {BLOCKS.map(({ icon: Icon, title, body }) => (
                <div key={title} className="flex gap-4">
                  <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-brand-plum">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* right: device status cards */}
          <div className="space-y-5 lg:pt-10">
            {/* Ready */}
            <div className="overflow-hidden rounded-2xl border border-success/30 bg-success/5">
              <div className="flex items-center gap-2 border-b border-success/20 px-5 py-3">
                <CheckCircle2 className="h-5 w-5 text-success" />
                <span className="font-display font-semibold text-brand-plum">Ready for processing</span>
                <span className="ml-auto rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold text-success">
                  Green
                </span>
              </div>
              <div className="px-5 py-4 text-sm">
                <div className="font-semibold text-brand-plum">Dell Latitude 5420</div>
                <dl className="mt-3 grid grid-cols-3 gap-3">
                  {[
                    ["Driver profile", "Found"],
                    ["MDM risk", "Low"],
                    ["Storage health", "Passed"],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-white p-2.5">
                      <dt className="text-[11px] text-muted">{k}</dt>
                      <dd className="font-medium text-success">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-muted">
                  <span className="font-medium text-brand-plum">Recommended action:</span> Proceed to erasure
                  and reimage.
                </p>
              </div>
            </div>

            {/* Blocked */}
            <div className="overflow-hidden rounded-2xl border border-danger/30 bg-danger/5">
              <div className="flex items-center gap-2 border-b border-danger/20 px-5 py-3">
                <XCircle className="h-5 w-5 text-danger" />
                <span className="font-display font-semibold text-brand-plum">Blocked</span>
                <span className="ml-auto rounded-full bg-danger/15 px-2.5 py-0.5 text-xs font-semibold text-danger">
                  Red
                </span>
              </div>
              <div className="px-5 py-4 text-sm">
                <div className="font-semibold text-brand-plum">HP EliteBook 840 G7</div>
                <p className="mt-3 text-muted">
                  <span className="font-medium text-brand-plum">Reason:</span> Possible MDM / ownership risk.
                </p>
                <p className="mt-2 text-muted">
                  <span className="font-medium text-brand-plum">Recommended action:</span> Move to quarantine
                  and request release confirmation.
                </p>
                <p className="mt-2 rounded-lg bg-danger/10 px-3 py-2 text-xs font-medium text-danger">
                  Processing: Do not erase or reimage yet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Preflight;
